import React from "react";
import { Box, Typography, Stack, Tooltip } from "@mui/material";
import GridAddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomSelect from "./common/CustomSelect";
import CustomTextField from "./common/CustomTextField";
import CustomButton from "./common/CustomButton";
import { COLORS } from "../assets/theme";

const VALUE_LESS_CONDITION_TYPES = ["ALFA", "NUMERIC", "ALPHANUMERIC", "URL"];

export interface ConditionValue {
  conditionType?: string;
  status?: string;
  value?: string;
  label?: string;
  conditionClause?: string;
  filterType?: string;
}

interface ConditionValuesFieldProps {
  type: "dropdown" | "text";
  conditionTypeOptions?: { label: string; value: string }[];
  valueOptions?: { label: string; value: string }[];
  clauseOptions?: { label: string; value: string }[];
  conditionStatuses?: ConditionValue[];
  errors?: Record<string, string>;
  label: string;
  sub?: boolean;
  handleConditionChange: (index: number, field: string, value: string) => void;
  handleAddCondition: () => void;
  handleRemoveCondition: (index: number) => void;
  whiteBackground?: boolean;
  fallback?: boolean;
  notIfCondition?: boolean;
  labelOptions?: { label: string; value: string }[];
  onLabelChange?: (index: number, value: string) => void;
  subLabelOptions?: { label: string; value: string }[];
  subLabelValue?: string[];
  onSubLabelChange?: (index: number, value: string) => void;
  showCaseSensitivityNote?: boolean;
  globalLabelOptions?: { label: string; value: string }[];
  globalLabelValue?: string;
  onGlobalLabelChange?: (value: string) => void;
  // Hint shown when hovering the value input, e.g. its expected format.
  valueTooltip?: string;
  valueMaxLength?: number;
}

const ConditionValuesField: React.FC<ConditionValuesFieldProps> = ({
  type,
  conditionTypeOptions = [],
  valueOptions = [],
  label,
  fallback,
  conditionStatuses = [],
  errors,
  handleConditionChange,
  handleAddCondition,
  handleRemoveCondition,
  onLabelChange,
  labelOptions,
  notIfCondition = false,
  whiteBackground = false,
  sub,
  subLabelOptions,
  subLabelValue,
  onSubLabelChange,
  showCaseSensitivityNote = false,
  globalLabelOptions,
  globalLabelValue,
  onGlobalLabelChange,
  valueTooltip,
  valueMaxLength,
}) => {
  const getErrorMessage = (fieldKey: string) => errors?.[fieldKey] || "";
  // The note sits next to the add/AND button, so that column needs room for it.
  const actionsWidth = showCaseSensitivityNote ? 76 : 48;

  return (
    <div style={{ width: "100%" }}>
      <Box>
        {!notIfCondition && (
          <Typography
            sx={{
              fontWeight: "500",
              fontSize: "16px",
              color: "#000",
              display: "flex",
              alignItems: "center",
            }}
          >
            If
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          </Typography>
        )}

        <Stack spacing={1}>
          {conditionStatuses.map((condition, index) => {
            const hidesValue = VALUE_LESS_CONDITION_TYPES.includes(
              condition.conditionType || "",
            );
            const isListCondition = condition.conditionType === "LIST";
            const selectedFieldOption =
              (sub && subLabelOptions
                ? subLabelOptions.find(
                    (option) => option.value === (subLabelValue?.[index] || ""),
                  )
                : undefined) ||
              labelOptions?.find((option) => option.value === condition.label);
            const hasFieldSelector = !!(
              (sub && subLabelOptions) ||
              labelOptions
            );
            const selectedLabel =
              selectedFieldOption?.label ||
              (hasFieldSelector ? "the selected field" : label);
            const conditionTypeLabel =
              conditionTypeOptions.find(
                (option) => option.value === condition.conditionType,
              )?.label || condition.conditionType;
            const isContentField = /content/i.test(selectedLabel);
            const isContainsCondition =
              (condition.conditionType || "").toUpperCase() === "CONTAINS";
            const caseSensitivityNote =
              !showCaseSensitivityNote || hidesValue
                ? ""
                : !condition.conditionType
                  ? '"Contains" is case-insensitive on content fields and case-sensitive on every other field. All other conditions are case-sensitive.'
                  : isContainsCondition && isContentField
                    ? `"${conditionTypeLabel}" on ${selectedLabel} is case-insensitive: upper and lower case are matched the same.`
                    : `"${conditionTypeLabel}" on ${selectedLabel} is case-sensitive: the value must match the exact letter casing.`;

            return (
              <Box
                key={index}
                display="flex"
                flexWrap={{ xs: "wrap", sm: "nowrap" }}
                sx={{
                  alignItems: "center",
                  width: "100%",
                  columnGap: 0,
                  rowGap: { xs: 1, sm: 0 },
                  mb: { xs: 1, sm: 0 },
                  overflowX: "visible",
                  "&:last-child": {
                    marginBottom: 0,
                  },
                }}
              >
                {labelOptions ? (
                  <Box
                    sx={{
                      width: { xs: "50%", sm: "auto" },
                      flex: { xs: "unset", sm: "1 1 110px" },
                      minWidth: 0,
                    }}
                  >
                    <CustomSelect
                      label=""
                      value={condition.label || ""}
                      options={labelOptions}
                      onChange={(value) => {
                        handleConditionChange(index, "label", value as string);
                        if (onLabelChange)
                          onLabelChange(index, value as string);
                      }}
                      placeholder={label}
                      size="small"
                      sx={{
                        minWidth: 0,
                        width: { xs: "100%", sm: "100%" },
                        height: "40px",
                        backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                        borderRadius: "12px 0 0 12px",
                        borderRight: {
                          xs: undefined,
                          sm: sub ? undefined : "none",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px 0 0 12px",
                          height: "40px",
                        },
                      }}
                    />
                  </Box>
                ) : globalLabelOptions ? (
                  index === 0 ? (
                    <CustomSelect
                      label=""
                      value={globalLabelValue || ""}
                      options={globalLabelOptions}
                      onChange={(value) => {
                        if (onGlobalLabelChange) onGlobalLabelChange(value as string);
                      }}
                      placeholder="Select Type"
                      size="small"
                      sx={{
                        minWidth: { xs: "100%", sm: "200px" },
                        width: { xs: "100%", sm: "auto" },
                        height: "40px",
                        backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                        borderRadius: { xs: "12px 12px 0 0", sm: "12px 0 0 12px" },
                        borderRight: "none",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: { xs: "12px 12px 0 0", sm: "12px 0 0 12px" },
                          height: "40px",
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        minWidth: { xs: "100%", sm: "200px" },
                        width: { xs: "100%", sm: "auto" },
                        height: "39px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        color: "#3C3C43",
                        backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                        borderRadius: { xs: "12px 12px 0 0", sm: "12px 0 0 12px" },
                        border: "0px solid #868585",
                        borderRight: "none",
                        fontWeight: "500",
                      }}
                    >
                      {globalLabelOptions.find((o) => o.value === globalLabelValue)?.label || globalLabelValue || label}
                    </Typography>
                  )
                ) : (
                  !sub && (
                    <Box
                      sx={{
                        width: { xs: "50%", sm: "auto" },
                        flex: { xs: "unset", sm: "1 1 100px" },
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 0,
                          width: "100%",
                          height: "39px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          color: "#3C3C43",
                          backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                          borderRadius: "12px 0 0 12px",
                          border: "0px solid #868585",
                          borderRight: { xs: undefined, sm: "none" },
                          fontWeight: "500",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  )
                )}

                {sub && subLabelOptions && (
                  <Box
                    sx={{
                      width: { xs: "50%", sm: "auto" },
                      flex: { xs: "unset", sm: "1 1 100px" },
                      minWidth: 0,
                    }}
                  >
                    <CustomSelect
                      label=""
                      value={subLabelValue ? subLabelValue[index] || "" : ""}
                      options={subLabelOptions}
                      onChange={(value) => {
                        if (onSubLabelChange)
                          onSubLabelChange(index, value as string);
                      }}
                      placeholder="Sub Label"
                      size="small"
                      sx={{
                        minWidth: 0,
                        width: "100%",
                        height: "40px",
                        backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                        borderRadius: { xs: "0", sm: "12px 0 0 12px" },
                        borderRight: {
                          xs: undefined,
                          sm: sub ? undefined : "none",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: { xs: "0", sm: "12px 0 0 12px" },
                          height: "40px",
                        },
                      }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    width: {
                      xs: hidesValue ? `calc(50% - ${actionsWidth}px)` : "50%",
                      sm: "auto",
                    },
                    flex: { xs: "unset", sm: "1 1 90px" },
                    minWidth: 0,
                  }}
                >
                  <CustomSelect
                    label=""
                    value={condition.conditionType || ""}
                    options={
                      condition.label === "userId"
                        ? conditionTypeOptions.filter(
                            (option) => option.value !== "Contains",
                          )
                        : conditionTypeOptions
                    }
                    onChange={(value) => {
                      handleConditionChange(
                        index,
                        "conditionType",
                        value as string,
                      );
                      if (
                        VALUE_LESS_CONDITION_TYPES.includes(value as string)
                      ) {
                        handleConditionChange(
                          index,
                          condition.status !== undefined ? "status" : "value",
                          "",
                        );
                      }
                    }}
                    placeholder="Condition"
                    size="small"
                    error={
                      !!getErrorMessage(
                        `conditionStatuses.${index}.conditionType`,
                      )
                    }
                    helperText={getErrorMessage(
                      `conditionStatuses.${index}.conditionType`,
                    )}
                    sx={{
                      maxHeight: "40px",
                      minWidth: 0,
                      width: "100%",
                      backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                      borderRadius: {
                        xs: "0 12px 12px 0",
                        sm: hidesValue ? "0 18px 18px 0" : 0,
                      },
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        borderRadius: {
                          xs: "0 12px 12px 0",
                          sm: hidesValue ? "0 18px 18px 0" : 0,
                        },
                      },
                    }}
                  />
                </Box>

                {!hidesValue && (
                  <Tooltip
                    title={valueTooltip || ""}
                    arrow
                    placement="top"
                    disableHoverListener={!valueTooltip}
                    disableFocusListener={!valueTooltip}
                    disableTouchListener={!valueTooltip}
                  >
                    <Box
                      sx={{
                        width: {
                          xs: `calc(100% - ${actionsWidth}px)`,
                          sm: "auto",
                        },
                        flex: { xs: "unset", sm: "1 1 130px" },
                        minWidth: 0,
                      }}
                    >
                      {type === "dropdown" || condition.label === "userId" ? (
                        <CustomSelect
                          label=""
                          value={condition.status || condition.value || ""}
                          options={valueOptions}
                          onChange={(value) =>
                            handleConditionChange(
                              index,
                              condition.status !== undefined ? "status" : "value",
                              value as string,
                            )
                          }
                          placeholder="Select Value"
                          size="small"
                          error={
                            !!getErrorMessage(
                              `conditionStatuses.${index}.${
                                condition.status !== undefined
                                  ? "status"
                                  : "value"
                              }`,
                            )
                          }
                          sx={{
                            minWidth: 0,
                            width: "100%",
                            height: "39px",
                            borderRadius: {
                              xs: "12px 0 0 12px",
                              sm: "0 18px 18px 0",
                            },
                            backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                          }}
                        />
                      ) : (
                        <CustomTextField
                          type="text"
                          fullWidth
                          minWidth="0"
                          placeholder={
                            isListCondition ? "Value1,Value2,Value3" : "Value"
                          }
                          value={condition.status || condition.value || ""}
                          inputProps={
                            valueMaxLength ? { maxLength: valueMaxLength } : undefined
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            handleConditionChange(
                              index,
                              condition.status !== undefined ? "status" : "value",
                              inputValue,
                            );
                          }}
                          error={
                            !!getErrorMessage(
                              `conditionStatuses.${index}.${
                                condition.status !== undefined
                                  ? "status"
                                  : "value"
                              }`,
                            )
                          }
                          sx={{
                            minWidth: 0,
                            width: "100%",
                            borderRadius: {
                              xs: "12px 0 0 12px",
                              sm: "0 15px 15px 0",
                            },
                            backgroundColor: whiteBackground ? "#fff" : "#EDEDED",
                            "& .MuiOutlinedInput-root fieldset": {
                              borderLeft: { xs: undefined, sm: "none" },
                              borderTopLeftRadius: { xs: undefined, sm: 0 },
                              borderBottomLeftRadius: { xs: undefined, sm: 0 },
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                )}

                <Box
                  sx={{
                    width: { xs: `${actionsWidth}px`, sm: "auto" },
                    flex: {
                      xs: "unset",
                      sm: showCaseSensitivityNote ? "0 0 68px" : "0 0 40px",
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {conditionStatuses.length === 1 ||
                  index === conditionStatuses.length - 1 ? (
                    <CustomButton
                      bgColor={COLORS.GOLD}
                      textColor={COLORS.textPrimary}
                      borderRadius="8px"
                      onClick={handleAddCondition}
                      isIconOnly
                      sx={{
                        width: "40px",
                        height: "40px",
                        minWidth: "40px",
                        marginLeft: { xs: "8px", sm: "2px" },
                        mt: 0,
                      }}
                    >
                      {fallback ? "AND" : <GridAddIcon fontSize="small" />}
                    </CustomButton>
                  ) : (
                    <CustomButton
                      bgColor={COLORS.GOLD}
                      textColor={COLORS.textPrimary}
                      borderRadius="8px"
                      onClick={() => handleRemoveCondition(index)}
                      isIconOnly
                      sx={{
                        height: "40px",
                        minWidth: "40px",
                        marginLeft: { xs: "8px", sm: 0 },
                        mt: 0,
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </CustomButton>
                  )}
                  {caseSensitivityNote && (
                    <Tooltip title={caseSensitivityNote} arrow placement="top">
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{
                          ml: "8px",
                          color: "#868585",
                          cursor: "help",
                          "&:hover": { color: COLORS.GOLD },
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </div>
  );
};

export default ConditionValuesField;
